import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

/* ------------------------------ helpers ------------------------------ */
const str = (x) => (x == null ? "" : String(x));
const key = (x) => String(x ?? "");
const sameId = (a, b) => key(a) === key(b);
const sameDoc = (it, id) => sameId(it?._id ?? it?.id, id);
const toIdSet = (ids) => new Set((ids || []).map(key));

const pickData = (res) => res?.data ?? res?.entry ?? res;

const toNum = (v) => Number(v ?? 0) || 0;
const round2 = (x) => Math.round((x + Number.EPSILON) * 100) / 100;

const normLimit = (args, draft) =>
  Number(args?.limit ?? draft?.pagination?.limit ?? draft?.limit ?? 10) || 10;

/* ----------------------------- totals utils ----------------------------- */
const incTotals = (draft, by = 1) => {
  if (draft?.pagination?.totalDocs != null) {
    draft.pagination.totalDocs += by;
    const lim = draft?.pagination?.limit || draft?.limit || 10;
    draft.pagination.totalPages = Math.max(
      1,
      Math.ceil(draft.pagination.totalDocs / lim),
    );
  } else if (draft?.total != null) {
    draft.total += by;
    const lim = draft?.limit || draft?.pagination?.limit || 10;
    draft.pages = Math.max(1, Math.ceil(draft.total / lim));
  }
};

const decTotals = (draft, by = 1) => {
  if (draft?.pagination?.totalDocs != null) {
    draft.pagination.totalDocs = Math.max(0, draft.pagination.totalDocs - by);
    const lim = draft?.pagination?.limit || draft?.limit || 10;
    draft.pagination.totalPages = Math.max(
      1,
      Math.ceil(draft.pagination.totalDocs / lim),
    );
  } else if (draft?.total != null) {
    draft.total = Math.max(0, draft.total - by);
    const lim = draft?.limit || draft?.pagination?.limit || 10;
    draft.pages = Math.max(1, Math.ceil(draft.total / lim));
  }
};

/* --------------------------- list manipulation --------------------------- */
const addIntoListDraft = (draft, args, newItems = []) => {
  if (!Array.isArray(draft?.rows)) draft.rows = [];
  if (!newItems?.length) return;

  if (args.page && Number(args.page) !== 1) {
    incTotals(draft, newItems.length);
    return;
  }

  draft.rows.unshift(...newItems);

  const lim = normLimit(args, draft);
  if (lim > 0 && draft.rows.length > lim) {
    draft.rows.splice(lim);
  }
  incTotals(draft, newItems.length);
};

/* --------------------------- filter matching --------------------------- */
const matchesFilters = (urd = {}, args = {}) => {
  const {
    branchId = "",
    counterId = "",
    customerId = "",
    from = "",
    to = "",
    search = "", // client-side only
  } = args || {};

  const rowBranchId = urd?.branchId?._id ?? urd?.branchId;
  const rowCounterId = urd?.counterId?._id ?? urd?.counterId;
  const rowCustomerId = urd?.customerId?._id ?? urd?.customerId;

  if (branchId && !sameId(rowBranchId, branchId)) return false;
  if (counterId && !sameId(rowCounterId, counterId)) return false;
  if (customerId && !sameId(rowCustomerId, customerId)) return false;

  if (from || to) {
    const d = new Date(urd.urdDate);
    if (from && d < new Date(from)) return false;
    if (to && d > new Date(to)) return false;
  }

  if (search) {
    const hay = [
      String(urd?.urdNo ?? ""),
      urd?.notes,
      urd?.customerId?.customerName,
      urd?.customerId?.customerId,
      urd?.branchId?.branchName,
      urd?.counterId?.counterName,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    if (!hay.includes(search.toLowerCase())) return false;
  }

  return true;
};

/* ----------------------- compute totals (client) ----------------------- */
const computeTotals = (entries = [], payments = []) => {
  let rows = entries.length;
  let qty = 0,
    totalWt = 0,
    packing = 0,
    gross = 0,
    stoneWt = 0,
    diaCt = 0,
    diaGm = 0,
    netWt = 0,
    fineWt = 0,
    amount = 0;

  for (const it of entries) {
    qty += toNum(it?.qty);
    const tw = toNum(it?.totalWt);
    const pw = toNum(it?.packingWt);
    const stw = toNum(it?.stoneWt);
    const dct = toNum(it?.diamondWt);
    const dgm = dct * 0.2;

    totalWt += tw;
    packing += pw;
    gross += tw - pw;
    stoneWt += stw;
    diaCt += dct;
    diaGm += dgm;

    const nw = tw - pw - stw - dgm;
    netWt += nw;
    fineWt += toNum(it?.fineWt);
    amount += toNum(it?.amount);
  }

  const paid = payments.reduce((s, p) => s + toNum(p?.amount), 0);
  const outstanding = round2(amount - paid);

  return {
    items: rows,
    qty: Math.round(qty),
    totalWt: +totalWt.toFixed(3),
    packingWt: +packing.toFixed(3),
    grossWt: +gross.toFixed(3),
    stoneWt: +stoneWt.toFixed(3),
    diamondCt: +diaCt.toFixed(3),
    diamondGram: +diaGm.toFixed(3),
    netWt: +netWt.toFixed(3),
    fineWt: +fineWt.toFixed(3),
    amount: round2(amount),
    paid: round2(paid),
    outstanding,
  };
};

/* ------------------- default args + cache seed/upsert ------------------- */
const defaultListArgs = (fromArgs = {}) => ({
  page: Number(fromArgs.page) || 1,
  limit: Number(fromArgs.limit) || 10,
  branchId: fromArgs.branchId || "",
  counterId: fromArgs.counterId || "",
  customerId: fromArgs.customerId || "",
  from: fromArgs.from || "",
  to: fromArgs.to || "",
  search: fromArgs.search || "",
  sortBy: fromArgs.sortBy,
  sortOrder: fromArgs.sortOrder,
});

const ensureListCacheAndInject = (dispatch, getState, args, rows) => {
  const a = defaultListArgs(args);
  const sel = urdApi.endpoints.getUrds.select(a)(getState());
  if (sel?.data) {
    dispatch(
      urdApi.util.updateQueryData("getUrds", a, (draft) => {
        addIntoListDraft(draft, a, rows);
      }),
    );
  } else {
    const lim = normLimit(a);
    const draft = { rows: [], total: 0, page: a.page, limit: lim };
    addIntoListDraft(draft, a, rows);
    dispatch(urdApi.util.upsertQueryData("getUrds", a, draft));
  }
};

const upsertOrPatchRow = (dispatch, getState, args, updatedRow) => {
  const a = defaultListArgs(args);
  const sel = urdApi.endpoints.getUrds.select(a)(getState());
  if (sel?.data) {
    dispatch(
      urdApi.util.updateQueryData("getUrds", a, (draft) => {
        if (!Array.isArray(draft?.rows)) draft.rows = [];
        const idx = draft.rows.findIndex((it) => sameDoc(it, updatedRow?._id));
        if (idx >= 0) {
          draft.rows[idx] = { ...draft.rows[idx], ...updatedRow };
        } else if (matchesFilters(updatedRow, a)) {
          addIntoListDraft(draft, a, [updatedRow]);
        }
      }),
    );
  } else if (matchesFilters(updatedRow, a)) {
    const lim = normLimit(a);
    dispatch(
      urdApi.util.upsertQueryData("getUrds", a, {
        rows: [updatedRow].slice(0, lim),
        total: 1,
        page: a.page,
        limit: lim,
      }),
    );
  }
};

/* -------------------- build optimistic row from form -------------------- */
const buildTempUrdRow = (payload, tempId) => {
  const nowIso = new Date().toISOString();

  const entries = Array.isArray(payload?.urdEntries)
    ? payload.urdEntries.map((r) => ({
        qty: toNum(r?.qty),
        totalWt: toNum(r?.totalWt),
        packingWt: toNum(r?.packingWt),
        stoneWt: toNum(r?.stoneWt),
        diamondWt: toNum(r?.diamondWt),
        netWt: toNum(r?.netWt),
        fineWt: toNum(r?.fineWt),
        amount: toNum(r?.amount),
      }))
    : [];

  const payments = Array.isArray(payload?.payments)
    ? payload.payments.map((p) => ({ amount: toNum(p?.amount) }))
    : [];

  const totals = computeTotals(entries, payments);

  const branch = payload?.branch;
  const counter = payload?.counter;
  const customer = payload?.customer;

  return {
    _id: tempId,
    urdNo: toNum(payload?.urdNo) || payload?.urdNo || undefined,
    urdDate: payload?.urdDate
      ? new Date(payload.urdDate).toISOString()
      : nowIso,
    settlement: payload?.settlement || "Wallet",
    notes: payload?.notes || "",
    branchId: branch
      ? { _id: branch.value, branchName: branch.label }
      : payload?.branchId
        ? { _id: payload.branchId }
        : undefined,
    counterId: counter
      ? { _id: counter.value, counterName: counter.label }
      : payload?.counterId
        ? { _id: payload.counterId }
        : undefined,
    customerId: customer
      ? {
          _id: customer.value,
          customerName: customer?.info?.customerName || customer?.label || "",
          customerId: customer?.info?.customerId || customer?.label || "",
          balanceAmount: customer?.info?.balanceAmount,
        }
      : payload?.customerId
        ? { _id: payload.customerId }
        : undefined,
    totals,
    payments,
    isActive: true,
    __temp: true,
  };
};

/* --------------------------------------------------------------------- */

export const urdApi = createApi({
  reducerPath: "urdApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL,
    credentials: "include",
  }),
  tagTypes: ["URD", "URD_LAST_NO", "URD_REPORT"],
  endpoints: (builder) => ({
    /* ------------------------------ LIST ------------------------------ */
    // GET /urd?branchId=&counterId=&customerId=&from=&to=&page=&limit=
    getUrds: builder.query({
      query: (args = {}) => {
        const {
          page = 1,
          limit = 10,
          branchId = "",
          counterId = "",
          customerId = "",
          from = "",
          to = "",
          search = "", // client-only; kept in key
          sortBy,
          sortOrder,
        } = args;

        const params = { page, limit };
        if (branchId) params.branchId = branchId;
        if (counterId) params.counterId = counterId;
        if (customerId) params.customerId = customerId;
        if (from) params.from = from;
        if (to) params.to = to;
        if (sortBy) params.sortBy = sortBy;
        if (sortOrder) params.sortOrder = sortOrder;

        return { url: "/urd", method: "GET", params };
      },
      providesTags: ["URD"],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const a = queryArgs || {};
        return [
          endpointName,
          a.page,
          a.limit,
          a.branchId,
          a.counterId,
          a.customerId,
          a.from,
          a.to,
          a.search,
          a.sortBy,
          a.sortOrder,
        ].join("|");
      },
      keepUnusedDataFor: 60,
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    }),

    /* ----------------------------- GET ONE --------------------------- */
    // GET /urd/:id
    getUrdById: builder.query({
      query: (id) => `/urd/${id}`,
      providesTags: (_res, _err, id) => [{ type: "URD", id: key(id) }],
    }),

    /* ----------------------------- CREATE ---------------------------- */
    // POST /urd
    addUrd: builder.mutation({
      query: (payload) => ({
        url: "/urd",
        method: "POST",
        body: payload,
      }),
      async onQueryStarted(payload, { dispatch, queryFulfilled, getState }) {
        const tempId = `tmp_${Date.now()}`;
        const tempRow = buildTempUrdRow(payload, tempId);

        const undoPatches = [];
        const allArgs = urdApi.util.selectCachedArgsForQuery(
          getState(),
          "getUrds",
        );
        for (const args of allArgs) {
          if (!matchesFilters(tempRow, args)) continue;
          undoPatches.push(
            dispatch(
              urdApi.util.updateQueryData("getUrds", args, (draft) => {
                addIntoListDraft(draft, args, [tempRow]);
              }),
            ),
          );
        }

        // ensure default cache (page 1)
        ensureListCacheAndInject(dispatch, getState, { page: 1, limit: 10 }, [
          tempRow,
        ]);

        try {
          const { data: res } = await queryFulfilled;
          const created = pickData(res);
          const real = Array.isArray(created) ? created[0] : created;
          if (!real) return;

          const again = urdApi.util.selectCachedArgsForQuery(
            getState(),
            "getUrds",
          );
          for (const args of again) {
            dispatch(
              urdApi.util.updateQueryData("getUrds", args, (draft) => {
                if (!Array.isArray(draft?.rows)) return;
                const pos = draft.rows.findIndex((r) => sameDoc(r, tempId));
                const nowMatches = matchesFilters(real, args);

                if (pos >= 0) {
                  if (nowMatches) {
                    draft.rows[pos] = { ...draft.rows[pos], ...real };
                    delete draft.rows[pos].__temp;
                  } else {
                    draft.rows.splice(pos, 1);
                    decTotals(draft, 1);
                  }
                } else if (nowMatches) {
                  addIntoListDraft(draft, args, [real]);
                }
              }),
            );
          }
        } catch {
          undoPatches.forEach((p) => p?.undo && p.undo());
        }
      },
      invalidatesTags: ["URD_LAST_NO"],
    }),

    /* ----------------------------- UPDATE ---------------------------- */
    // PUT /urd/:id
    updateUrd: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/urd/${id}`,
        method: "PUT",
        body: patch,
      }),
      async onQueryStarted(
        { id, ...patch },
        { dispatch, queryFulfilled, getState },
      ) {
        const patches = [];
        const allArgs = urdApi.util.selectCachedArgsForQuery(
          getState(),
          "getUrds",
        );

        for (const args of allArgs) {
          patches.push(
            dispatch(
              urdApi.util.updateQueryData("getUrds", args, (draft) => {
                if (!Array.isArray(draft?.rows)) return;
                const idx = draft.rows.findIndex((it) => sameDoc(it, id));
                if (idx >= 0) {
                  draft.rows[idx] = { ...draft.rows[idx], ...patch };
                }
              }),
            ),
          );
        }

        patches.push(
          dispatch(
            urdApi.util.updateQueryData("getUrdById", id, (draft) => {
              const doc = pickData(draft);
              if (doc?.data) Object.assign(doc.data, patch);
              if (draft?.data) Object.assign(draft.data, patch);
            }),
          ),
        );

        try {
          const { data: res } = await queryFulfilled;
          const updated = pickData(res);
          if (!updated) return;

          for (const args of allArgs) {
            dispatch(
              urdApi.util.updateQueryData("getUrds", args, (draft) => {
                if (!Array.isArray(draft?.rows)) return;
                const idx = draft.rows.findIndex((it) => sameDoc(it, id));
                const nowMatches = matchesFilters(updated, args);

                if (idx >= 0) {
                  if (nowMatches) {
                    draft.rows[idx] = { ...draft.rows[idx], ...updated };
                  } else {
                    draft.rows.splice(idx, 1);
                    decTotals(draft, 1);
                  }
                } else if (nowMatches) {
                  addIntoListDraft(draft, args, [updated]);
                }
              }),
            );
          }

          upsertOrPatchRow(dispatch, getState, { page: 1, limit: 10 }, updated);

          dispatch(
            urdApi.util.updateQueryData("getUrdById", id, (draft) => {
              if (draft?.data) Object.assign(draft.data, updated);
            }),
          );
        } catch {
          patches.forEach((p) => p?.undo && p.undo());
        }
      },
    }),

    /* ----------------------------- DELETE ---------------------------- */
    // DELETE /urd/:id
    deleteUrd: builder.mutation({
      query: (id) => ({
        url: `/urd/${id}`,
        method: "DELETE",
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        const patches = [];
        const allArgs = urdApi.util.selectCachedArgsForQuery(
          getState(),
          "getUrds",
        );

        for (const args of allArgs) {
          patches.push(
            dispatch(
              urdApi.util.updateQueryData("getUrds", args, (draft) => {
                if (!Array.isArray(draft?.rows)) return;
                const before = draft.rows.length;
                draft.rows = draft.rows.filter((it) => !sameDoc(it, id));
                const removed = before - draft.rows.length;
                if (removed > 0) decTotals(draft, removed);
              }),
            ),
          );
        }

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p?.undo && p.undo());
        }
      },
      invalidatesTags: ["URD_LAST_NO"],
    }),

    /* -------------------------- BULK DELETE -------------------------- */
    // POST /urd/bulk-delete  (body: { ids: [...] })
    bulkDeleteUrds: builder.mutation({
      query: (ids) => ({
        url: `/urd/bulk-delete`,
        method: "POST",
        body: { ids },
      }),
      async onQueryStarted(ids, { dispatch, queryFulfilled, getState }) {
        const patches = [];
        const idSet = toIdSet(ids);
        const allArgs = urdApi.util.selectCachedArgsForQuery(
          getState(),
          "getUrds",
        );

        for (const args of allArgs) {
          patches.push(
            dispatch(
              urdApi.util.updateQueryData("getUrds", args, (draft) => {
                if (!Array.isArray(draft?.rows)) return;
                const before = draft.rows.length;
                draft.rows = draft.rows.filter(
                  (it) => !idSet.has(key(it?._id ?? it?.id)),
                );
                const removed = before - draft.rows.length;
                if (removed > 0) decTotals(draft, removed);
              }),
            ),
          );
        }

        try {
          await queryFulfilled;
        } catch {
          patches.forEach((p) => p?.undo && p.undo());
        }
      },
      invalidatesTags: ["URD_LAST_NO"],
    }),

    /* ------------------------- LAST URD NUMBER ------------------------ */
    // GET /urd/last-number?branchId=&date=
    getLastUrdNumber: builder.query({
      query: (args = {}) => {
        const { branchId = "", date = "" } = args || {};
        const params = {};
        if (branchId) params.branchId = branchId;
        if (date) params.date = date;
        return { url: "/urd/last-number", method: "GET", params };
      },
      providesTags: ["URD_LAST_NO"],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const a = queryArgs || {};
        return [endpointName, a.branchId || "", a.date || ""].join("|");
      },
      keepUnusedDataFor: 30,
      refetchOnMountOrArgChange: true,
    }),

    /* ----------------------------- REPORT ---------------------------- */
    // GET /urd/report/simple?branchId=&counterId=&categoryId=&productId=&from=&to=&page=&limit=
    getUrdReport: builder.query({
      query: (args = {}) => {
        const {
          branchId = "",
          counterId = "",
          categoryId = "",
          productId = "",
          from = "",
          to = "",
          page = 1,
          limit = 20,
        } = args || {};

        const params = { page, limit };
        if (branchId) params.branchId = branchId;
        if (counterId) params.counterId = counterId;
        if (categoryId) params.categoryId = categoryId;
        if (productId) params.productId = productId;
        if (from) params.from = from;
        if (to) params.to = to;

        return { url: "/urd/report/simple", method: "GET", params };
      },
      providesTags: ["URD_REPORT"],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const a = queryArgs || {};
        return [
          endpointName,
          a.branchId,
          a.counterId,
          a.categoryId,
          a.productId,
          a.from,
          a.to,
          a.page,
          a.limit,
        ].join("|");
      },
      keepUnusedDataFor: 60,
      refetchOnMountOrArgChange: true,
    }),

    downloadUrdReceiptPdf: builder.mutation({
      query: (id) => ({
        url: `/urd/${id}/receipt.pdf`,
        method: "GET",
        responseHandler: (response) => response.text(),
      }),
    }),
  }),
});

/* ----------------------------- hooks ----------------------------- */
export const {
  useGetUrdsQuery,
  useGetUrdByIdQuery,
  useAddUrdMutation,
  useUpdateUrdMutation,
  useDeleteUrdMutation,
  useBulkDeleteUrdsMutation,
  useGetLastUrdNumberQuery,
  useGetUrdReportQuery,
  useDownloadUrdReceiptPdfMutation,
} = urdApi;

export default urdApi;
